import { useEffect, useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import { LOCALES, LOCALE_LABELS, LOCALE_SHORT, type Locale } from '@diana/shared'
import { LocaleProvider, localePath, pathWithoutLocale } from '../../i18n/index.js'
import { COMMON } from '../../i18n/common.js'
import { useSession } from '../../lib/auth.js'

export function PublicLayout({ locale }: { locale: Locale }) {
  return (
    <LocaleProvider locale={locale}>
      <Chrome locale={locale} />
    </LocaleProvider>
  )
}

function Chrome({ locale }: { locale: Locale }) {
  const [open, setOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const c = COMMON[locale]
  const to = (path: string) => localePath(locale, path)

  // Diana, signed in, gets a way back to the panel from any public page.
  // `role` is an additional better-auth field, so it isn't on the client type.
  const { data: session } = useSession()
  const isAdmin = (session?.user as { role?: string } | undefined)?.role === 'ADMIN'

  const nav = [
    { to: to('/o-mnie'), label: c.nav.about },
    { to: to('/ucz-sie-ze-mna'), label: c.nav.learn },
    { to: to('/produkty'), label: c.nav.products },
    { to: to('/kontakt'), label: c.nav.contact },
  ]

  // The header gains a hairline and a shadow once you leave the top, so it
  // reads as floating above the page rather than welded to it.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Lock the page behind the mobile menu, but restore scrolling on unmount —
  // a stuck `overflow: hidden` on body would also break the sticky header.
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  return (
    <div className="flex min-h-screen flex-col">
      <header
        className={[
          'sticky top-0 z-40 transition-all duration-300',
          scrolled
            ? 'border-b border-gold/25 bg-ivory/92 shadow-[0_10px_30px_-24px_rgba(30,42,53,0.45)] backdrop-blur-md'
            : 'border-b border-transparent bg-ivory/80 backdrop-blur-sm',
        ].join(' ')}
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-6 px-5 py-4 lg:px-8">
          <Link to={to('/')} className="group flex flex-col leading-none">
            <span className="h-display text-2xl tracking-[0.18em] text-ink">DIANA</span>
            <span className="mt-1 font-roboto text-[0.6rem] uppercase tracking-[0.3em] text-gold-600">
              {c.tagline}
            </span>
          </Link>

          <nav className="hidden items-center gap-8 lg:flex">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  ['nav-link', isActive ? 'text-gold-600' : 'text-ink hover:text-gold-600'].join(' ')
                }
              >
                {item.label}
              </NavLink>
            ))}
            <LanguageSwitcher locale={locale} />
            <Link to={to('/rezerwacja')} className="btn btn-gold btn-sm">
              {c.bookLesson}
            </Link>
            {isAdmin && (
              <Link to="/studio" className="btn btn-outline btn-sm">
                {c.adminPanel}
              </Link>
            )}
          </nav>

          <div className="flex items-center gap-5 lg:hidden">
            <LanguageSwitcher locale={locale} />
            <button
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? c.menuClose : c.menuOpen}
              aria-expanded={open}
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" d={open ? 'M6 18L18 6M6 6l12 12' : 'M3 7h18M3 12h18M3 17h18'} />
              </svg>
            </button>
          </div>
        </div>

        {open && (
          <nav className="border-t border-gold/20 bg-ivory px-5 pb-6 pt-2 lg:hidden">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  [
                    'block border-b border-sky-100 py-3.5 font-roboto text-sm font-bold uppercase tracking-[0.14em]',
                    isActive ? 'text-gold-600' : 'text-ink',
                  ].join(' ')
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to={to('/rezerwacja')}
              onClick={() => setOpen(false)}
              className="btn btn-gold btn-sm mt-6 w-full"
            >
              {c.bookLesson}
            </Link>
            {isAdmin && (
              <Link to="/studio" onClick={() => setOpen(false)} className="btn btn-outline btn-sm mt-3 w-full">
                {c.adminPanel}
              </Link>
            )}
          </nav>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <Footer locale={locale} />
    </div>
  )
}

/**
 * PL | UA | RU. Switching keeps the visitor on the same page — only the
 * prefix changes — so the choice never costs them their place.
 */
function LanguageSwitcher({ locale }: { locale: Locale }) {
  const { pathname } = useLocation()
  const base = pathWithoutLocale(pathname)
  const c = COMMON[locale]

  return (
    <nav aria-label={c.switchLanguage} className="flex items-center gap-1 font-roboto text-xs font-bold tracking-[0.14em]">
      {LOCALES.map((l, i) => (
        <span key={l} className="flex items-center gap-1">
          {i > 0 && <span aria-hidden className="text-sky-200">|</span>}
          <Link
            to={localePath(l, base)}
            lang={l}
            aria-current={l === locale ? 'true' : undefined}
            title={LOCALE_LABELS[l]}
            className={l === locale ? 'text-gold-600' : 'text-ink-faint transition hover:text-gold-600'}
          >
            {LOCALE_SHORT[l]}
          </Link>
        </span>
      ))}
    </nav>
  )
}

function Footer({ locale }: { locale: Locale }) {
  const c = COMMON[locale]
  const to = (path: string) => localePath(locale, path)

  // Diana, signed in, gets a way back to the panel from any public page.
  // `role` is an additional better-auth field, so it isn't on the client type.
  const { data: session } = useSession()
  const isAdmin = (session?.user as { role?: string } | undefined)?.role === 'ADMIN'

  const nav = [
    { to: to('/o-mnie'), label: c.nav.about },
    { to: to('/ucz-sie-ze-mna'), label: c.nav.learn },
    { to: to('/produkty'), label: c.nav.products },
    { to: to('/kontakt'), label: c.nav.contact },
  ]

  return (
    <footer className="mt-32 bg-ink text-sky-100">
      <div className="mx-auto max-w-6xl px-5 py-16 lg:px-8">
        <div className="grid gap-12 md:grid-cols-[1.4fr_1fr_1fr]">
          <div>
            <p className="h-display text-3xl tracking-[0.18em] text-gold-300">DIANA</p>
            <div className="rule-gold mt-4 w-24" />
            <p className="mt-5 max-w-sm text-sm leading-relaxed font-light text-sky-200/80">
              {c.footer.bio}
            </p>
          </div>

          <nav>
            <p className="font-roboto text-[0.7rem] font-bold uppercase tracking-[0.2em] text-gold-300">
              {c.footer.navigation}
            </p>
            <ul className="mt-5 space-y-2.5 text-sm font-light">
              {nav.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className="text-sky-200/80 transition hover:text-gold-300">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div>
            <p className="font-roboto text-[0.7rem] font-bold uppercase tracking-[0.2em] text-gold-300">
              {c.footer.start}
            </p>
            <ul className="mt-5 space-y-2.5 text-sm font-light">
              <li>
                <Link to={to('/rezerwacja')} className="text-sky-200/80 transition hover:text-gold-300">
                  {c.footer.freeCall}
                </Link>
              </li>
              <li>
                <Link to={to('/ucz-sie-ze-mna')} className="text-sky-200/80 transition hover:text-gold-300">
                  {c.footer.pricing}
                </Link>
              </li>
              <li>
                <Link to="/login" className="text-sky-200/60 transition hover:text-gold-300">
                  {c.footer.panel}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col gap-3 border-t border-sky-100/10 pt-7 text-xs font-light text-sky-200/50 sm:flex-row sm:justify-between">
          <p>© {new Date().getFullYear()} Diana — {c.tagline}</p>
          <p className="flex flex-wrap gap-x-3 gap-y-1">
            <Link to={to('/regulamin')} className="transition hover:text-gold-300">
              {c.footer.terms}
            </Link>
            <span aria-hidden>·</span>
            <Link to={to('/prywatnosc')} className="transition hover:text-gold-300">
              {c.footer.privacy}
            </Link>
            <span aria-hidden>·</span>
            <Link to={to('/cookies')} className="transition hover:text-gold-300">
              {c.footer.cookies}
            </Link>
          </p>
        </div>
      </div>
    </footer>
  )
}
